import { useState, useEffect, useCallback, useContext, useRef, memo } from 'react';
import { Button, Tooltip, Typography } from '@scale/scaleui';
import {
  DEFAULT_MODEL_EDITOR_SUBTITLE,
  DEFAULT_MODEL_EDITOR_TITLE,
  InteractionStepType,
  ModelResponseEditorStepResponse,
  ModelResponseEditorType,
} from '@scale/shared/types/chat';
import { extractVariablesFromPreviousResponses } from '../../../utils';
import useModelResponseEditor from './useModelResponseEditor';
import {
  CardAlert,
  AutoCritiqueButton,
  AutoCritiqueAlert,
  Alert,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/TempComponents';
import AutoFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback';
import clsx from 'clsx';
import { useTrans } from '../../../hooks/useTrans';
import useAutoCritique from '../../../hooks/useAutoCritique';
import useAutoTyperDetector from '../../../hooks/useAutoTyperDetector';
import { useRightToLeft } from '../../../hooks/useAutoLanguageDetection';
import { useAttemptBehaviorLog } from '../../../contexts/AttemptBehaviorLogContext';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import _ from 'lodash';
import { ReviewLevel } from '@scale/shared/defaults';
import {
  memoizedMostRecentPrompt,
  useChatStoreExtract,
  useChatStoreRef,
  useMostRecentPromptAttachments,
  useStepParams,
  useStepResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import ContentTypeSelector, { Mode } from '../components/ContentTypeSelector';
import { useStepSubmitHandler } from '../../../hooks/useStepSubmitHandler';
import TwoTextSxSDiffView from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/TwoTextSxSDiffView';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { StepCard } from '../../../step/StepCard';
import { CommentButton } from '../../../feedback/CommentButton';
import { ResponseTemplatingButton } from '../../../step/ResponseTemplatingButton';
import { PinButton } from '../../../step/PinButton';
import useAutoFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/hooks/useAutoFeedback';
import { getAutoFeedbackStatus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/helpers';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { AttachmentViewer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AttachmentViewer';
import { ChatIFrame } from '../../../ChatIFrame';
import {
  Editor,
  EditorContainer,
  FooterContainer,
  PlainTextEditorContext,
  WordCounter,
  MarkdownFloatingMenu,
  PlainTextFloatingMenu,
} from '../../../components/editor';
import MarkdownEditorContext from '../../../components/editor/MarkdownEditorContext';
import MarkdownToolbar from '../../../components/editor/toolbar/MarkdownToolbar';
import WorkerCommentBridge from './WorkerCommentBridge';
import { InlineCommentPopover } from './InlineCommentPopover';
import { TextSelectionPopover } from './TextSelectionPopover';
import { useModelResponseEditorStepValidation } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseEditor/utils';
import ReviewerCopilotPopover from '../../../components/editor/widgets/ReviewerCopilot';
import { InteractionStepTaskComponent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/InteractionStep';
import {
  StepWidthWrapper,
  StepWidth,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepWidthWrapper';
import { ChatLazarusUIRenderer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatLazarusUIRenderer';
import { ChatHtmlRenderer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatHtmlRenderer';
import { useMarkdownSyntaxOptions } from '../../../hooks/useMarkdownSyntaxOptions';

const ModelResponseEditorTaskComponent: InteractionStepTaskComponent =
  // eslint-disable-next-line complexity
  ({ readOnly, complete, responseIndex, isPinnedView }) => {
    const taskContext = useContext(ChatTaskContext);
    const chatStoreRef = useChatStoreRef();
    const config = useChatStoreExtract(state => state.config);
    const currentMultiChatIndex = useChatStoreExtract(state => state.currentMultiChatIndex);
    const shouldResetHistory = taskContext.forceHistoryResetForResponseChanges;

    const [enforcementPassed, setEnforcementPassed] = useState<boolean>(true);

    const markdownSyntaxConfig = useMarkdownSyntaxOptions();

    const stepParams = useStepParams<InteractionStepType.ModelResponseEditor>(responseIndex);
    const response = useStepResponse<ModelResponseEditorStepResponse>(responseIndex);
    const { onSubmit } = useStepSubmitHandler({
      responseIndex,
    });

    const [editorMode, setEditorMode] = useState<Mode>('editor');

    const enablePlaintextEditor = !!taskContext.editorConfig?.enablePlaintextEditor;
    const editorType = enablePlaintextEditor
      ? ModelResponseEditorType.Plaintext
      : ModelResponseEditorType.Markdown;
    const editorModeOptions: Mode[] = [ModelResponseEditorType.Markdown].includes(editorType)
      ? ['editor', 'diff']
      : ['editor', 'diff', 'preview']; // Only plaintext needs the markdown preview

    const markStepAsIncompletedByIndex = useChatStoreExtract(
      state => state.markStepAsIncompletedByIndex,
    );
    const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

    const { emitTypingEvent, setLatestValue, incAutotyperDetectedEvent, incMouseLeaveEvent } =
      useAttemptBehaviorLog();
    const { t } = useTrans();

    const {
      text,
      setText,
      clearBackup,
      baseResponse,
      originalText,
      handlePassUpdateToMilkdown,
      resAttachments,
      setResAttachments,
      reactUIGeneratedUrl,
      setReactUIGeneratedUrl,
      reactUIGeneratedUrlWithMetadata,
      setReactUIGeneratedUrlWithMetadata,
      lastResponseObject,
      originalResponseMessageMetadata,
    } = useModelResponseEditor(responseIndex);
    const lazarusModel = !_.isNil(lastResponseObject?.context?.selectedIndex)
      ? lastResponseObject?.context?.candidates?.[lastResponseObject?.context?.selectedIndex]
          ?.endpoint?.params?.model
      : undefined;

    const [rightToLeft, setRightToLeft] = useState<boolean>(
      stepParams.params.enable_rtl_text ?? false,
    );

    const enableAutomaticLanguageDetection =
      !!taskContext.languageConfig?.enableAutomaticLanguageDetection;
    const enableRTLToggle =
      !!taskContext.languageConfig?.enableRightToLeftToggleInEditor ||
      enableAutomaticLanguageDetection;
    const detectedRightToLeft = useRightToLeft({
      enabled: enableAutomaticLanguageDetection,
      text,
      queryKey: [
        taskContext.projectId,
        taskContext.assignmentId,
        taskContext.workerId,
        responseIndex.toString(),
      ],
      taskId: taskContext.taskId ?? '',
      projectId: taskContext.projectId ?? '',
      baseLocalSaveKey: `RTL-${taskContext.assignmentId}-${responseIndex}`,
    });

    useEffect(() => {
      if (enableAutomaticLanguageDetection) {
        setRightToLeft(detectedRightToLeft);
      } else {
        setRightToLeft(stepParams.params.enable_rtl_text ?? false);
      }
    }, [enableAutomaticLanguageDetection, stepParams.params.enable_rtl_text, detectedRightToLeft]);

    const [showDisablePastingAlert, setShowDisablePastingAlert] = useState<boolean>(false);
    const disablePasting = !!taskContext.disableResponsePasting;
    const onDetectAutoTyper = useCallback(() => {
      incAutotyperDetectedEvent({
        fieldId: 'responseText',
        responseIndex,
      });
      if (disablePasting && !taskContext.disableAutoTyperTextRemoval) {
        setText('');
        handlePassUpdateToMilkdown('');
        setShowDisablePastingAlert(true);
      }
    }, [
      setText,
      disablePasting,
      handlePassUpdateToMilkdown,
      incAutotyperDetectedEvent,
      responseIndex,
      taskContext.disableAutoTyperTextRemoval,
    ]);
    const autoTyperDetectorHook = useAutoTyperDetector(onDetectAutoTyper);

    const cardRef = useRef<HTMLDivElement>(null);

    const childId = constructStepChildId(stepParams);

    const autoFeedbackHook = useAutoFeedback({
      id: `${taskContext.assignmentId ?? taskContext.taskId}-${childId}`,
      responseIndex,
      reviewLevel,
      stepType: stepParams.type,
      stepId: stepParams.id,
      multichatIndex: currentMultiChatIndex,
    });

    // AutoFeedback does not pass if it hasn't been run
    // If it errors, we still allow the user to submit
    const autoFeedbackStatus = getAutoFeedbackStatus(autoFeedbackHook ?? undefined);
    const attachments = useMostRecentPromptAttachments(responseIndex);

    const autoCritiqueHook = useAutoCritique(response?.context.autoCritiqueResponse);
    const [hasRanAutoCritique, setHasRanAutoCritique] = useState<boolean>(false);
    const autoCritiqueConfig = taskContext.autoCritiqueConfig;
    const excludeReviewLevels: ReviewLevel[] | undefined = autoCritiqueConfig?.excludeReviewLevels;

    const enforceAutoCritique =
      !!autoCritiqueConfig?.enforceCritique &&
      autoCritiqueConfig?.modelResponseEditor?.enabled &&
      !excludeReviewLevels?.includes(reviewLevel);
    const blockSevereCritique =
      autoCritiqueConfig?.enableSeverityBlocking?.blockCriticalSeverity &&
      !excludeReviewLevels?.includes(reviewLevel);

    const shouldUseAutoFeedback =
      !taskContext.enableUnifiedFeedback &&
      !autoFeedbackHook?.isFetchingStepCompatible &&
      (autoFeedbackHook?.compatibleSteps.includes(InteractionStepType.ModelResponseEditor) ||
        autoFeedbackHook?.compatibleSteps.includes(stepParams?.id));

    const { toolTipText } = useModelResponseEditorStepValidation({
      autoCritiqueHook,
      autoFeedbackHook,
      autoFeedbackStatus,
      text,
      autoCritiqueConfig,
      enforceAutoCritique: !!enforceAutoCritique,
      hasRanAutoCritique,
      blockSevereCritique: !!blockSevereCritique,
      shouldUseAutoFeedback: !!shouldUseAutoFeedback,
    });

    const autoCritiqueLabel = taskContext.autoCritiqueConfig?.modelResponseEditor?.renameButton;

    const fetchAutoCritique = useCallback(async () => {
      const currentState = chatStoreRef.current.getState();
      const responses = currentState.responses;
      const lastPrompt = memoizedMostRecentPrompt(currentState, responseIndex);
      const validPrompt = lastPrompt?.length;
      const validPromptTemplate = autoCritiqueConfig?.modelResponseEditor?.promptTemplate?.length;
      if (validPrompt || validPromptTemplate) {
        await autoCritiqueHook.getAutoCritique({
          scope: 'modelResponseEditor',
          projectId: taskContext.projectId,
          taskId: taskContext.taskId,
          assignmentId: taskContext.assignmentId,
          workerId: taskContext.workerId,
          variables: {
            ...config.templateVariables,
            ...extractVariablesFromPreviousResponses(responses), // TODO: @Lajos extract this into a memoized store hook!
            prompt: lastPrompt,
            response: text,
          },
        });
      }
      setHasRanAutoCritique(true);
    }, [
      chatStoreRef,
      responseIndex,
      autoCritiqueConfig?.modelResponseEditor?.promptTemplate,
      autoCritiqueHook,
      taskContext.projectId,
      taskContext.taskId,
      taskContext.assignmentId,
      taskContext.workerId,
      config.templateVariables,
      text,
    ]);

    const editorReadOnly = readOnly || complete || !!autoFeedbackHook?.isFetchingFeedback;

    const handleOnTextChange = useCallback(
      (text: any) => {
        emitTypingEvent({
          fieldId: 'responseText',
          text,
          responseIndex,
        });
        if (text !== '') {
          setShowDisablePastingAlert(false);
        }
        setText(text);
        autoTyperDetectorHook.updateKeystrokeTimestamp();
      },
      [setText, autoTyperDetectorHook, emitTypingEvent, responseIndex],
    );

    useEffect(() => {
      setLatestValue({
        fieldId: 'responseText',
        text: baseResponse,
        responseIndex,
      });
    }, [setLatestValue, responseIndex, baseResponse]);

    const handleMouseLeave = useCallback(() => {
      incMouseLeaveEvent({
        fieldId: 'responseText',
        responseIndex,
      });
    }, [incMouseLeaveEvent, responseIndex]);

    const editorComponents = (
      <>
        {editorType === ModelResponseEditorType.Markdown && (
          <>
            <MarkdownEditorContext
              defaultTokens={markdownSyntaxConfig}
              initialContent={text}
              enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
            >
              <EditorContainer>
                <MarkdownToolbar enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin} />
                <MarkdownFloatingMenu
                  enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                />
                <Editor
                  content={text}
                  onContentChange={handleOnTextChange}
                  disablePaste={disablePasting}
                  onDisabledPaste={() => setShowDisablePastingAlert(true)}
                  enableRTL={rightToLeft}
                  onMouseLeave={handleMouseLeave}
                  readOnly={editorReadOnly}
                />
                <FooterContainer>
                  {taskContext.enableWordCounter && <WordCounter />}
                </FooterContainer>
              </EditorContainer>
              <ReviewerCopilotPopover anchorRef={cardRef} enabled={taskContext.enableFactChecker} />
              <WorkerCommentBridge
                enabled={!!stepParams.params.enable_inline_comments}
                childId={childId}
              />
              <InlineCommentPopover
                anchorRef={cardRef}
                enableEditorClickAway={editorReadOnly}
                childId={childId}
              />
              <TextSelectionPopover
                enabled={
                  !!stepParams.params.enable_inline_comments &&
                  !!taskContext.canEditComments &&
                  editorReadOnly
                }
                anchorRef={cardRef}
                childId={childId}
                turnIndex={stepParams.turn}
              />
            </MarkdownEditorContext>
          </>
        )}
        {editorType === ModelResponseEditorType.Plaintext && (
          <PlainTextEditorContext initialContent={text}>
            <EditorContainer>
              <Editor
                readOnly={editorReadOnly}
                enableRTL={rightToLeft}
                content={text}
                onContentChange={handleOnTextChange}
                disablePaste={disablePasting}
                onDisabledPaste={() => setShowDisablePastingAlert(true)}
                onMouseLeave={handleMouseLeave}
              />
              <PlainTextFloatingMenu />
              <FooterContainer>{taskContext.enableWordCounter && <WordCounter />}</FooterContainer>
            </EditorContainer>
            <ReviewerCopilotPopover anchorRef={cardRef} enabled={taskContext.enableFactChecker} />
          </PlainTextEditorContext>
        )}
      </>
    );

    return (
      <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
        <CardAlert message={response && !complete ? 'Please save the section below.' : undefined}>
          <StepCard readOnly={readOnly}>
            <StepCard.Header>
              <StepCard.HeaderLeft>
                <StepCard.Icon icon="edit" />
                <StepCard.HeaderColumn>
                  <StepCard.Title>{stepParams.title || DEFAULT_MODEL_EDITOR_TITLE}</StepCard.Title>
                  <StepCard.Subtitle>
                    {stepParams.subtitle || DEFAULT_MODEL_EDITOR_SUBTITLE}
                  </StepCard.Subtitle>
                </StepCard.HeaderColumn>
              </StepCard.HeaderLeft>
              <StepCard.HeaderRight>
                {!taskContext.enableUnifiedFeedback && (
                  <CommentButton visible={true} childId={childId} turnIndex={stepParams.turn} />
                )}
                <ResponseTemplatingButton
                  childId={childId}
                  metadata={{ stepIndex: responseIndex }}
                />
                <PinButton responseIndex={responseIndex} />
              </StepCard.HeaderRight>
            </StepCard.Header>
            <StepCard.SubHeaderRow>
              <StepCard.SubHeader>
                <ContentTypeSelector
                  selectedMode={editorMode}
                  modeOptions={editorModeOptions}
                  onModeChange={setEditorMode}
                  showTextDirectionToggle={enableRTLToggle}
                  onTextDirectionChange={setRightToLeft}
                  rightToLeft={rightToLeft}
                />
              </StepCard.SubHeader>
            </StepCard.SubHeaderRow>
            <StepCard.Body>
              <div ref={cardRef} className={readOnly ? 'cursor-not-allowed' : ''}>
                {editorMode === 'editor' ? (
                  editorComponents
                ) : (
                  <>
                    {editorMode === 'diff' && (
                      <TwoTextSxSDiffView
                        textOne={originalText ?? response?.context?.originalText}
                        textTwo={text}
                        textOneTitle="Old"
                        textTwoTitle="New"
                        variant="stretch"
                        enableRTL={rightToLeft}
                      />
                    )}

                    {editorMode === 'preview' && (
                      <div
                        className={clsx(
                          'w-full border-neutral-200 overflow-hidden rounded-md min-h-[180px]',
                          {
                            border: true,
                          },
                        )}
                      >
                        <div className="px-4 py-3">
                          <ChatMarkdown
                            className="text-sm"
                            markdown={text}
                            enableRTL={stepParams.params.enable_rtl_text}
                            enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                            enableSingleDollarTextMath={
                              taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                            }
                            enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                            enableSoftBreaks={
                              taskContext.generalMarkdownConfig?.enableSoftLineBreaks
                            }
                            enableExecutableCodeBlocks={
                              taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                              taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                            }
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {config.renderCode && <ChatIFrame src={text} />}

                {resAttachments && resAttachments[0] && (
                  <AttachmentViewer
                    attachments={resAttachments}
                    setAttachments={setResAttachments}
                    useEditor={true}
                    canEdit={!editorReadOnly}
                  />
                )}
              </div>

              {showDisablePastingAlert && (
                <Alert variant="warning" className="my-4">
                  <Typography.Paragraph2>This field does not allow pasting.</Typography.Paragraph2>
                </Alert>
              )}

              <AutoCritiqueAlert
                autoCritiqueHook={autoCritiqueHook}
                headerLabel={autoCritiqueLabel}
              />

              {shouldUseAutoFeedback && (
                <AutoFeedback
                  autoFeedbackHook={autoFeedbackHook}
                  inputContent={text}
                  readOnly={readOnly || complete}
                  attachments={attachments}
                />
              )}

              {stepParams.params.enable_react_ui_rendering && (
                <ChatLazarusUIRenderer
                  src={text}
                  allowRegeneration
                  setContentUrl={setReactUIGeneratedUrl}
                  lazarusModel={lazarusModel}
                />
              )}

              {(config.showHtmlGen || stepParams.params.enable_ui_generation) && (
                <ChatHtmlRenderer
                  source="mre"
                  src={text}
                  enforcementMode={stepParams.params.ui_generation_enforcement_mode}
                  setEnforcementPassed={setEnforcementPassed}
                  contentUrlWithMetadata={reactUIGeneratedUrlWithMetadata}
                  setContentGeneratedUrlWithMetadata={newReactUIGeneratedUrlWithMetadata => {
                    setReactUIGeneratedUrlWithMetadata(newReactUIGeneratedUrlWithMetadata);
                    if (
                      !_.isEqual(
                        newReactUIGeneratedUrlWithMetadata,
                        reactUIGeneratedUrlWithMetadata,
                      )
                    ) {
                      markStepAsIncompletedByIndex(responseIndex);
                    }
                  }}
                  uid={`${stepParams.id}-${stepParams.turn}`}
                />
              )}
            </StepCard.Body>
            <StepCard.Footer className="flex justify-end gap-4 mt-2">
              {!readOnly && complete && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    const canUpdateCompletedState = response && !_.isNil(responseIndex);
                    if (canUpdateCompletedState) {
                      markStepAsIncompletedByIndex(responseIndex);
                    }
                  }}
                >
                  {t('edit-step')}
                </Button>
              )}
              {!editorReadOnly && (
                <>
                  <Tooltip
                    side="top"
                    text={toolTipText}
                    target={
                      <div className="flex gap-2">
                        {autoCritiqueConfig?.modelResponseEditor?.enabled && (
                          <AutoCritiqueButton
                            onClick={fetchAutoCritique}
                            loading={autoCritiqueHook.critiqueData.loading}
                            buttonLabel={autoCritiqueLabel}
                          />
                        )}
                        <Button
                          variant="primary"
                          onClick={async () => {
                            const autoCritiqueResponse = autoCritiqueHook?.critiqueData.critique;
                            const sentinelFeedback = autoFeedbackHook?.feedback;
                            const isValid = await onSubmit(
                              {
                                type: InteractionStepType.ModelResponseEditor,
                                context: {
                                  baseResponse,
                                  originalText,
                                  autoCritiqueResponse,
                                  attachments: resAttachments,
                                  reactUIGeneratedUrl,
                                  ...reactUIGeneratedUrlWithMetadata,
                                  messageMetadata: originalResponseMessageMetadata,
                                },
                                autoFeedbackResponse: {
                                  feedback: sentinelFeedback,
                                  status: autoFeedbackStatus,
                                },
                                output: text,
                              },
                              responseIndex,
                              stepParams,
                              shouldResetHistory,
                            );
                            if (!enforcementPassed) {
                              markStepAsIncompletedByIndex(responseIndex);
                            }
                            if (isValid) {
                              clearBackup();
                            }
                          }}
                          disabled={toolTipText !== ''}
                          data-testid="finish-editing-model-response-editor-button"
                        >
                          {t('finish-editing')}
                        </Button>
                      </div>
                    }
                  />
                </>
              )}
            </StepCard.Footer>
          </StepCard>
        </CardAlert>
      </StepWidthWrapper>
    );
  };

export default memo(ModelResponseEditorTaskComponent);
